import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Container, Paper, Tab, Tabs, Alert, Snackbar, Button, Typography, CircularProgress } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import CreateInvoiceForm from '../../components/Invoice/CreateInvoiceForm';
import InvoiceTemplate from '../../components/Invoice/InvoiceTemplate';
import ESignatureComponent from '../../components/Invoice/ESignatureComponent';
import { isInterstateTransaction } from '../../utils/indianStates';

const companyDetails = {
  name: '',
  logo: '',
  address: '',
  mobile: '',
  gstin: '',
  email: '',
};

const CreateInvoicePage = () => {
  const navigate = useNavigate();
  const { id: editInvoiceId } = useParams();
  const [currentTab, setCurrentTab] = useState(0);
  const [generatedInvoice, setGeneratedInvoice] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isLoading, setIsLoading] = useState(!!editInvoiceId);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState('success');
  const [initialFormData, setInitialFormData] = useState(null);

  // Debug useEffect to monitor state changes
  useEffect(() => {
    console.log('CreateInvoicePage - generatedInvoice state changed:', generatedInvoice);
    console.log('CreateInvoicePage - currentTab:', currentTab);
  }, [generatedInvoice, currentTab]);

  // Debug useEffect to monitor component lifecycle
  useEffect(() => {
    console.log('CreateInvoicePage - Component mounted');
    return () => {
      console.log('CreateInvoicePage - Component unmounting');
    };
  }, []);

  // If editing, fetch invoice and prefill form
  useEffect(() => {
    const fetchInvoiceForEdit = async () => {
      if (!editInvoiceId) return;
      try {
        setIsLoading(true);
        const { authFetch } = await import('../../utils/api');
        const res = await authFetch(`/invoices/${editInvoiceId}`);
        if (!res.ok) {
          console.error('Failed to fetch invoice:', res.status, res.statusText);
          return;
        }
        const data = await res.json();
        console.log('Fetched invoice data for edit:', data);

        // Map backend fields to form initialData shape
        const mapped = {
          companyDetails: {
            name: data.company_name,
            logo: data.company_logo || '', // Map logo if exists
            address: data.company_address,
            mobile: data.company_mobile,
            gstin: data.company_gstin,
            email: data.company_email,
            state: data.company_state || '',
          },
          bankDetails: {
            holderName: data.bank_holder_name,
            name: data.bank_name,
            accountNo: data.bank_account_number,
            ifscCode: data.bank_ifsc_code,
            branch: data.bank_branch,
          },
          billTo: {
            name: data.bill_to_name,
            address: data.bill_to_address,
            state: data.bill_to_state,
            mobile: data.bill_to_mobile,
            gstin: data.bill_to_gstin, // Added missing field
            customer_id: data.customer_id, // Added missing field
          },
          shipTo: {
            name: data.ship_to_name,
            address: data.ship_to_address,
            state: data.ship_to_state, // Added missing field
            mobile: data.ship_to_mobile,
            gstin: data.ship_to_gstin, // Added missing field if available
          },
          transportDetails: {
            vehicleNumber: data.vehicle_number,
            vehicleType: data.vehicle_type,
            driverName: data.driver_name,
            driverContact: data.driver_contact,
            ewayBillNumber: data.eway_bill_number, // Added missing field
          },
          items: (() => {
            let parsedItems = [];
            if (Array.isArray(data.items)) {
              parsedItems = data.items;
            } else if (typeof data.items === 'string') {
              try { parsedItems = JSON.parse(data.items); } catch(e) {}
            }
            return parsedItems.map(it => ({
              name: it.name || it.description,
              description: it.description || it.name,
              quantity: it.qty || it.quantity || 0,
              rate: it.rate || 0,
              tax: it.tax || it.gst || 0,
              discount: it.discount || 0,
              amount: it.amount || 0,
              hsn: it.hsn || '',
              inventoryId: it.inventoryId || null,
              unit: it.unit || 'PCS',
            }));
          })(),
          discount: Number(data.total_discount || 0), // Map total invoice discount
          originalFor: data.original_for || '',
          documentType: data.document_type || 'invoice',
          termsAndConditions: data.terms || '',
          invoiceDate: data.invoice_date,
          signatureUrl: data.signatureUrl || data.signature || '',
          theme: data.theme || 'classic',
          watermark: data.watermark || 'none',
        };
        console.log('Mapped initialFormData:', mapped);
        setInitialFormData(mapped);
        setCurrentTab(0);
      } catch (e) {
        console.error('Failed to load invoice for edit:', e);
        setAlertMessage('Failed to load invoice details.');
        setAlertSeverity('error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchInvoiceForEdit();
  }, [editInvoiceId]);

  // Removed preview flow; keep single create view

  const handleTabChange = () => { };

  const handleInvoiceGenerate = async (invoice) => {
    console.log('handleInvoiceGenerate called with invoice:', invoice);
    // Save directly from create tab without navigating to preview
    setIsCreating(true);
    try {
      const { authFetch } = await import('../../utils/api');

      // Upload signature first if it's a data URL, to reduce payload size
      let uploadedSignatureUrl = invoice.signatureUrl;
      let uploadedSignatureRaw = invoice.signature;
      if (!uploadedSignatureUrl && typeof invoice.signature === 'string' && invoice.signature.startsWith('data:')) {
        try {
          const blob = await fetch(invoice.signature).then(r => r.blob());
          const form = new FormData();
          form.append('signature', blob, `signature_${invoice.id || Date.now()}.jpg`);
          form.append('documentId', String(invoice.id || 'temp'));
          form.append('signedAt', new Date().toISOString());
          const up = await authFetch('/signatures', { method: 'POST', body: form });
          if (up.ok) {
            const upJs = await up.json();
            uploadedSignatureUrl = upJs?.data?.signatureUrl || upJs?.signatureUrl || null;
            uploadedSignatureRaw = uploadedSignatureUrl || uploadedSignatureRaw;
          }
        } catch (e) {
          console.error('Signature upload during generate failed:', e);
        }
      }

      // Map items to discounted pre-tax amounts
      const apiItems = (invoice.items || []).map(item => {
        const base = (item.quantity || 0) * (item.rate || 0);
        const discAmt = (base * (item.discount || 0)) / 100;
        const discounted = Math.max(0, base - discAmt);
        return ({
          description: item.description || item.name,
          quantity: item.quantity,
          rate: item.rate,
          gst: item.tax,
          discount: item.discount || 0,
          hsn: item.hsn || '',
          amount: discounted,
          inventoryId: item.inventoryId || null, // CRITICAL: Pass inventoryId
          unit: item.unit || 'PCS',
        });
      });

      // Subtotal/GST/Total consistent with backend expectations
      const subtotal = apiItems.reduce((s, it) => s + (Number(it.amount) || 0), 0);
      const totalTax = apiItems.reduce((s, it) => s + ((Number(it.amount) || 0) * (Number(it.gst) || 0) / 100), 0);
      
      const originState = (invoice.company?.state || invoice.company_state || '').trim();
      const destState = (invoice.billTo?.state || invoice.bill_to_state || '').trim();

      const isInter = Boolean(
        invoice.taxDetails?.isInterstate ||
        (invoice.taxDetails?.igst > 0) ||
        isInterstateTransaction(
          originState || invoice.company?.gstin,
          destState || invoice.billTo?.gstin
        )
      );

      let cgst = 0;
      let sgst = 0;
      let igst = 0;
      if (isInter) {
        igst = totalTax;
        cgst = 0;
        sgst = 0;
      } else {
        cgst = totalTax / 2;
        sgst = totalTax / 2;
        igst = 0;
      }
      const total = Math.round(subtotal + totalTax); // Rounded to nearest integer

      const payload = {
        invoice_date: invoice.invoiceDate,
        original_for: invoice.originalFor || "",
        document_type: invoice.documentType || 'invoice',
        status: invoice.status || 'finalized', // Add status field for inventory deduction

        bill_to_name: invoice.billTo.name,
        bill_to_address: invoice.billTo.address,
        bill_to_mobile: invoice.billTo.mobile,
        bill_to_state: invoice.billTo.state,
        bill_to_gstin: invoice.billTo.gstin,
        customer_id: invoice.billTo.customer_id,

        ship_to_name: invoice.shipTo?.name || invoice.billTo.name,
        ship_to_address: invoice.shipTo?.address || invoice.billTo.address,
        ship_to_mobile: invoice.shipTo?.mobile || invoice.billTo.mobile,
        ship_to_state: invoice.shipTo?.state || invoice.billTo.state,
        ship_to_gstin: invoice.shipTo?.gstin || invoice.billTo.gstin,

        company_name: invoice.company?.name || invoice.company_name,
        company_address: invoice.company?.address || invoice.company_address,
        company_gstin: invoice.company?.gstin || invoice.company_gstin,
        company_email: invoice.company?.email || invoice.company_email,
        company_mobile: invoice.company?.mobile || invoice.company_mobile,
        company_state: originState,

        items: apiItems,
        subtotal: subtotal.toFixed(2),
        gst: totalTax.toFixed(2),
        cgst: cgst.toFixed(2),
        sgst: sgst.toFixed(2),
        igst: igst.toFixed(2),
        total: total, // Send as integer

        terms: invoice.termsAndConditions,
        signature: uploadedSignatureUrl ? null : uploadedSignatureRaw,
        signatureUrl: uploadedSignatureUrl || invoice.signatureUrl,

        vehicle_number: invoice.transportDetails?.vehicleNumber || '',
        vehicle_type: invoice.transportDetails?.vehicleType || '',
        driver_name: invoice.transportDetails?.driverName || '',
        driver_contact: invoice.transportDetails?.driverContact || '',
        eway_bill_number: invoice.transportDetails?.ewayBillNumber || '',

        bank_name: invoice.bankDetails?.name,
        bank_account_number: invoice.bankDetails?.accountNo,
        bank_ifsc_code: invoice.bankDetails?.ifscCode,
        bank_branch: invoice.bankDetails?.branch,
        bank_holder_name: invoice.bankDetails?.holderName,

        theme: invoice.theme || 'classic',
        watermark: invoice.watermark || 'none',
        isDraft: false,
      };

      const controller = new AbortController();
      const isEdit = Boolean(editInvoiceId);
      const endpoint = isEdit ? `/invoices/${editInvoiceId}` : '/invoices';
      const method = isEdit ? 'PUT' : 'POST';

      console.log(`[CreateInvoicePage] 🚀 Sending ${method} request to ${endpoint}`);
      console.log(`[CreateInvoicePage] Payload:`, JSON.stringify(payload, null, 2));
      console.log(`[CreateInvoicePage] Items with inventoryId:`, payload.items.map(item => ({
        name: item.description || item.name,
        inventoryId: item.inventoryId,
        quantity: item.qty || item.quantity,
        hasInventoryId: !!item.inventoryId
      })));

      const resp = await authFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      if (!resp.ok) {
        let msg = `Failed to create invoice (HTTP ${resp.status})`;
        try {
          const data = await resp.json();
          const m = Array.isArray(data?.message) ? data.message.join(', ') : (data?.message || data?.error);
          if (m) msg = m;
        } catch (_) {
          try {
            const txt = await resp.text();
            if (txt) msg = txt;
          } catch (_) { }
        }
        throw new Error(msg);
      }
      const result = await resp.json();
      setAlertMessage(isEdit ? 'Invoice updated successfully! Redirecting in 3 seconds...' : 'Invoice created successfully! Redirecting in 5 seconds...');
      setAlertSeverity('success');
      setGeneratedInvoice({ ...invoice, isCreated: true, id: result.invoice_id || result.id, serverData: result });

      // Trigger inventory refresh across the app
      if (window.dispatchEvent) {
        window.dispatchEvent(new CustomEvent('inventoryUpdated', {
          detail: {
            type: 'deduction',
            invoiceId: result.invoice_id || result.id,
            items: apiItems.filter(item => item.inventoryId)
          }
        }));
      }

      // Redirect to invoices after a short delay
      setTimeout(() => {
        navigate('/invoices');
      }, isEdit ? 3000 : 5000);
    } catch (e) {
      console.error('Create invoice error:', e);
      setAlertMessage(e?.message || 'Failed to create invoice. Please try again.');
      setAlertSeverity('error');
    } finally {
      setIsCreating(false);
    }
  };

  const handleEdit = () => {
    setCurrentTab(0); // Switch back to form tab
  };

  const handleDelete = () => {
    setGeneratedInvoice(null);
    setCurrentTab(0);
  };

  const handleSign = (signature) => {
    if (generatedInvoice) {
      setGeneratedInvoice({
        ...generatedInvoice,
        signature,
        updatedAt: new Date().toISOString(),
      });
    }
  };

  const handleSignatureSubmit = (signatureData) => {
    if (generatedInvoice) {
      setGeneratedInvoice({
        ...generatedInvoice,
        signature: signatureData.signatureUrl,
        updatedAt: new Date().toISOString(),
      });
    }
  };

  const handlePrint = () => {
    console.log('Print requested for invoice:', generatedInvoice);
    if (generatedInvoice) {
      window.print();
    }
  };

  const handleCreateInvoice = async () => {
    if (!generatedInvoice || isCreating) return; // Prevent duplicate submissions

    setIsCreating(true);
    try {
      // Import the API functions
      const { authFetch } = await import('../../utils/api');

      // If signature is a data URL, upload it first to get a URL
      let uploadedSignatureUrl = generatedInvoice.signatureUrl;
      if (!uploadedSignatureUrl && generatedInvoice.signature && typeof generatedInvoice.signature === 'string' && generatedInvoice.signature.startsWith('data:')) {
        try {
          const respData = await (async () => {
            const dataUrl = generatedInvoice.signature;
            const blob = await fetch(dataUrl).then(r => r.blob());
            const form = new FormData();
            form.append('signature', blob, `signature_${generatedInvoice.id || Date.now()}.jpg`);
            form.append('documentId', String(generatedInvoice.id || 'temp'));
            form.append('signedAt', new Date().toISOString());
            const uploadResp = await authFetch('/signatures', { method: 'POST', body: form });
            if (!uploadResp.ok) throw new Error('Failed to upload signature');
            return uploadResp.json();
          })();
          uploadedSignatureUrl = respData?.data?.signatureUrl || respData?.signatureUrl;
        } catch (e) {
          console.error('Signature upload failed, proceeding without URL:', e);
        }
      }

      // Map the invoice to API format
      const apiPayload = {
        invoice_date: generatedInvoice.invoiceDate,
        original_for: generatedInvoice.originalFor || "",
        document_type: generatedInvoice.documentType || 'invoice',

        // Bill To details
        bill_to_name: generatedInvoice.billTo.name,
        bill_to_address: generatedInvoice.billTo.address,
        bill_to_mobile: generatedInvoice.billTo.mobile,
        bill_to_state: generatedInvoice.billTo.state,
        bill_to_gstin: generatedInvoice.billTo.gstin,
        customer_id: generatedInvoice.billTo.customer_id,

        // Ship To details
        ship_to_name: generatedInvoice.shipTo?.name || generatedInvoice.billTo.name,
        ship_to_address: generatedInvoice.shipTo?.address || generatedInvoice.billTo.address,
        ship_to_mobile: generatedInvoice.shipTo?.mobile || generatedInvoice.billTo.mobile,
        ship_to_state: generatedInvoice.shipTo?.state || generatedInvoice.billTo.state,
        ship_to_gstin: generatedInvoice.shipTo?.gstin || generatedInvoice.billTo.gstin,

        // Company details
        company_name: generatedInvoice.company.name,
        company_address: generatedInvoice.company.address,
        company_state: generatedInvoice.company?.state || generatedInvoice.company_state || '',
        company_gstin: generatedInvoice.company.gstin,
        company_email: generatedInvoice.company.email,
        company_mobile: generatedInvoice.company.mobile,

        // Items
        items: generatedInvoice.items.map(item => ({
          description: item.description || item.name,
          quantity: item.quantity,
          rate: item.rate,
          gst: item.tax,
          discount: item.discount || 0, // Add discount field
          hsn: item.hsn || '',
          amount: item.amount
        })),

        // Calculated totals
        subtotal: generatedInvoice.subtotal.toFixed(2),
        total_discount: generatedInvoice.totalDiscount || 0, // Add total discount
        gst: generatedInvoice.taxDetails.totalTax.toFixed(2),
        cgst: (generatedInvoice.taxDetails.cgst || 0).toFixed(2),
        sgst: (generatedInvoice.taxDetails.sgst || 0).toFixed(2),
        igst: (generatedInvoice.taxDetails.igst || 0).toFixed(2),
        isInterstate: Boolean(generatedInvoice.taxDetails?.isInterstate),
        total: Math.round(generatedInvoice.totalAmount), // Round total to nearest integer

        // Terms and signature
        terms: generatedInvoice.termsAndConditions,
        signature: uploadedSignatureUrl || generatedInvoice.signature,
        signatureUrl: uploadedSignatureUrl || generatedInvoice.signatureUrl,

        // Transport details for E-way bill
        vehicle_number: generatedInvoice.transportDetails?.vehicleNumber || '',
        vehicle_type: generatedInvoice.transportDetails?.vehicleType || '',
        driver_name: generatedInvoice.transportDetails?.driverName || '',
        driver_contact: generatedInvoice.transportDetails?.driverContact || '',

        // Bank details
        bank_name: generatedInvoice.bankDetails?.name,
        bank_account: generatedInvoice.bankDetails?.accountNo,
        bank_ifsc: generatedInvoice.bankDetails?.ifscCode,
        bank_branch: generatedInvoice.bankDetails?.branch,
        bank_holder_name: generatedInvoice.bankDetails?.holderName,

        isDraft: false, // Mark as final invoice
      };

      const response = await authFetch('/invoices', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiPayload),
      });

      if (!response.ok) {
        let backendMsg = 'Failed to create invoice';
        try {
          const errJson = await response.json();
          if (errJson && (errJson.message || errJson.error)) {
            backendMsg = Array.isArray(errJson.message) ? errJson.message.join(', ') : (errJson.message || errJson.error);
          }
        } catch (_) { }
        throw new Error(backendMsg);
      }

      const result = await response.json();
      console.log('Invoice created successfully:', result);
      console.log('Setting invoice as created, staying on preview page...');

      setAlertMessage('Invoice created successfully! You can now view, download, or print your invoice.');
      setAlertSeverity('success');

      // Update the invoice with the server response data
      setGeneratedInvoice(prev => {
        const updated = {
          ...prev,
          id: result.invoice_id || result.id,
          serverData: result,
          isCreated: true
        };
        console.log('Updated invoice state:', updated);
        return updated;
      });

      // Don't auto-redirect - let user stay on preview page
      console.log('No auto-redirect - user will stay on preview page');

      // Force stay on preview tab
      setCurrentTab(1);

    } catch (error) {
      console.error('Error creating invoice:', error);
      setAlertMessage(error?.message || 'Failed to create invoice. Please try again.');
      setAlertSeverity('error');
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header with Back Button */}
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/invoices')}
          variant="outlined"
          sx={{ mr: 2 }}
        >
          Back to Invoices
        </Button>
        <Typography variant="h4" component="h1">
          {editInvoiceId ? 'Edit Invoice' : 'Create New Invoice'}
        </Typography>
      </Box>

      <Paper sx={{ p: 2 }}>
        {/* Preview removed; only Create screen remains */}

        <Box sx={{ mt: 3 }}>
          {isLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
              <CircularProgress />
            </Box>
          ) : (
            <CreateInvoiceForm
              onSubmit={handleInvoiceGenerate}
              companyDetails={companyDetails}
              initialData={initialFormData}
              invoiceId={editInvoiceId}
            />
          )}
        </Box>
      </Paper>

      {/* Success/Error Alert */}
      <Snackbar
        open={!!alertMessage}
        autoHideDuration={4000}
        onClose={() => setAlertMessage('')}
      >
        <Alert
          onClose={() => setAlertMessage('')}
          severity={alertSeverity}
          sx={{ width: '100%' }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CreateInvoicePage;
